import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useLoad } from "@/lib/hooks";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAccess } from "@/context/AuthContext";
import { PageHeader, Section, StatusBadge, Field, NativeSelect } from "@/components/common";
import { AFRICAN_COUNTRIES, COUNTRY_NAME } from "@/lib/countries";

const blank = { name: "", address: { street: "", city: "", postal_code: "" }, country: "", phone: "", manager_user_id: "", status: "active" };

export default function Branches() {
  const [d, reload] = useLoad("/tenant/branches");
  const [users] = useLoad("/tenant/users");
  const [f, setF] = useState(null);
  const [upgrade, setUpgrade] = useState(null);
  const { canWrite } = useAccess();
  const can = canWrite(["tenant_admin"]);
  if (!d) return null;
  const atLimit = d.active >= d.limit;
  const save = async (body = f) => {
    const payload = { name: body.name, address: body.address || {}, country: body.country || null, phone: body.phone || null, manager_user_id: body.manager_user_id || null, status: body.status };
    try {
      await (body.id ? api.put(`/tenant/branches/${body.id}`, payload) : api.post("/tenant/branches", payload));
      toast.success("Branch saved"); setF(null); reload();
    } catch (e) {
      if (e.response?.status === 402) setUpgrade(errMsg(e)); else toast.error(errMsg(e));
    }
  };
  const setA = (k) => (e) => setF({ ...f, address: { ...f.address, [k]: e.target.value } });
  return (
    <>
      <PageHeader title="Branches" subtitle={`Physical locations or business units · ${d.active} of ${d.limit} active branches on your plan`}>
        {can && <Button data-testid="branch-add-button" onClick={() => (atLimit ? setUpgrade(`Your plan allows ${d.limit} active branch(es).`) : setF({ ...blank }))} className="bg-[#45BFB4] text-[#13294B] hover:bg-[#389F95] hover:text-white"><Plus className="mr-1 h-4 w-4" />Add branch</Button>}
      </PageHeader>
      <Section>
        <div className="overflow-x-auto"><table className="po-table" data-testid="branches-table">
          <thead><tr><th>Branch</th><th>Address</th><th>Country</th><th>Phone</th><th>Manager</th><th>Status</th><th></th></tr></thead>
          <tbody>{d.branches.map((b) => (
            <tr key={b.id} data-testid={`branch-row-${b.id}`}>
              <td className="font-medium">{b.name}{b.is_main && <span className="ml-2 rounded bg-[#E6F8F6] px-1.5 text-[10px] font-semibold uppercase text-[#13294B]">main</span>}</td>
              <td className="text-sm text-slate-600">{[b.address?.street, b.address?.city, b.address?.postal_code].filter(Boolean).join(", ") || "—"}</td>
              <td>{COUNTRY_NAME[b.country] || "—"}</td><td>{b.phone || "—"}</td><td>{b.manager_name || "—"}</td>
              <td><StatusBadge status={b.status === "active" ? "active" : "canceled"} label={b.status} /></td>
              <td className="space-x-1 whitespace-nowrap text-right">{can && <>
                <Button size="sm" variant="outline" data-testid={`branch-edit-${b.id}`} onClick={() => setF({ ...blank, ...b, address: { ...blank.address, ...b.address }, manager_user_id: b.manager_user_id || "", country: b.country || "", phone: b.phone || "" })}><Pencil className="h-3.5 w-3.5" /></Button>
                {!b.is_main && <Button size="sm" variant="ghost" data-testid={`branch-toggle-${b.id}`} onClick={() => save({ ...b, status: b.status === "active" ? "inactive" : "active" })}>{b.status === "active" ? "Deactivate" : "Activate"}</Button>}</>}</td>
            </tr>))}</tbody>
        </table></div>
      </Section>
      <Dialog open={!!f} onOpenChange={() => setF(null)}>
        <DialogContent data-testid="branch-dialog">
          <DialogHeader><DialogTitle>{f?.id ? "Edit branch" : "New branch"}</DialogTitle><DialogDescription>New invoices, jobs, POS sales and expenses can be tagged to this branch.</DialogDescription></DialogHeader>
          {f && <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Name"><Input data-testid="branch-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
            <Field label="Phone"><Input data-testid="branch-phone-input" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
            <Field label="Street"><Input data-testid="branch-street-input" value={f.address.street || ""} onChange={setA("street")} /></Field>
            <Field label="City"><Input data-testid="branch-city-input" value={f.address.city || ""} onChange={setA("city")} /></Field>
            <Field label="Postal code"><Input data-testid="branch-postal-input" value={f.address.postal_code || ""} onChange={setA("postal_code")} /></Field>
            <Field label="Country"><NativeSelect testId="branch-country-select" value={f.country} onChange={(v) => setF({ ...f, country: v })} placeholder="Select country" options={AFRICAN_COUNTRIES.map(([n, c]) => ({ value: c, label: n }))} /></Field>
            <Field label="Manager"><NativeSelect testId="branch-manager-select" value={f.manager_user_id} onChange={(v) => setF({ ...f, manager_user_id: v })} placeholder="No manager" options={(users || []).map((u) => ({ value: u.id, label: u.name }))} /></Field>
          </div>}
          <DialogFooter><Button data-testid="branch-save-button" disabled={!f?.name} onClick={() => save()}>Save branch</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!upgrade} onOpenChange={() => setUpgrade(null)}>
        <DialogContent data-testid="branch-upgrade-dialog">
          <DialogHeader><DialogTitle>Branch limit reached</DialogTitle><DialogDescription>{upgrade} Upgrade your plan or add the "Extra branch" add-on to open more branches.</DialogDescription></DialogHeader>
          <DialogFooter><Button asChild data-testid="branch-upgrade-link" className="bg-[#13294B]"><Link to="/app/billing">Go to Billing <ArrowUpRight className="ml-1 h-4 w-4" /></Link></Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
